/**
 * The admin's modules, and which of them this platform has switched on.
 *
 * A marketplace does not need every module on day one — a seller-only
 * operation has no use for POS, a single-vendor store none for Vendor SaaS —
 * and an admin who cannot hide what they do not use is left navigating past
 * it every day. This is the one list of what can be hidden, read by the
 * sidebar to decide what to draw and by Settings → Modules to decide what to
 * offer. It is stored as a website setting, so it survives deploys and is
 * the same for every admin.
 *
 * Some modules cannot be hidden. Hiding Settings would hide the switch that
 * un-hides everything; hiding Orders or Sellers would leave a marketplace
 * with no way to run itself. Those are `locked` and always on.
 */
import { create } from 'zustand';

import { api } from './api';

export const MODULES_SETTING_KEY = 'admin_modules';

export interface ModuleDef {
  /** Stable id; what the setting stores. Never rename one. */
  id: string;
  label: string;
  /** One line on what it is for, shown in Settings. */
  purpose: string;
  /** Sidebar section it lives under, for grouping in Settings. */
  section: string;
  /** Sidebar routes this module owns. A route not listed here is never hidden. */
  routes: string[];
  /** Always on; the toggle is shown disabled with the reason. */
  locked?: string;
}

export const MODULES: ModuleDef[] = [
  // ── Catalog ──
  {
    id: 'notes',
    label: 'Notes',
    purpose: 'Internal notes on products and customers.',
    section: 'Catalog',
    routes: ['/notes'],
  },
  {
    id: 'agents',
    label: 'Agents',
    purpose: 'Referral agents and their payouts.',
    section: 'Catalog',
    routes: ['/agents'],
  },
  {
    id: 'reports',
    label: 'Reports',
    purpose: 'Sales, earnings and wallet reports.',
    section: 'Catalog',
    routes: ['/reports'],
  },
  {
    id: 'blog',
    label: 'Blog System',
    purpose: 'Posts and categories on the storefront blog.',
    section: 'Catalog',
    routes: ['/blog'],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    purpose: 'Coupons, flash deals, newsletters, popups.',
    section: 'Catalog',
    routes: ['/marketing'],
  },
  {
    id: 'sponsored',
    label: 'Sponsored Control Center',
    purpose: 'Paid placements sellers buy on the storefront.',
    section: 'Catalog',
    routes: ['/sponsored'],
  },
  {
    id: 'support',
    label: 'Support',
    purpose: 'Tickets and customer conversations.',
    section: 'Catalog',
    routes: ['/support'],
  },
  // ── Finance ──
  {
    id: 'wallet',
    label: 'Wallet',
    purpose: 'Customer credits and vendor reserve holds.',
    section: 'Finance',
    routes: ['/wallet'],
  },
  // ── Engagement ──
  {
    id: 'crm',
    label: 'CRM',
    purpose: 'Segments and campaigns.',
    section: 'Engagement',
    routes: ['/crm'],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    purpose: 'Templates and the delivery dashboard.',
    section: 'Engagement',
    routes: ['/notifications', '/notifications/delivery'],
  },
  // ── Operations ──
  {
    id: 'erp',
    label: 'ERP',
    purpose: 'Suppliers, purchase orders, goods receipts.',
    section: 'Operations',
    routes: ['/erp'],
  },
  {
    id: 'pos',
    label: 'POS',
    purpose: 'Point-of-sale terminals, shifts and cash.',
    section: 'Operations',
    routes: ['/pos'],
  },
  {
    id: 'logistics',
    label: 'Logistics',
    purpose: 'Shipments, NDR and courier reconciliation.',
    section: 'Operations',
    routes: ['/logistics'],
  },
  {
    id: 'b2b',
    label: 'B2B',
    purpose: 'Business accounts, RFQs, contracts and credit.',
    section: 'Operations',
    routes: ['/b2b'],
  },
  {
    id: 'saas',
    label: 'Vendor SaaS',
    purpose: 'Seller subscription plans and limits.',
    section: 'Operations',
    routes: ['/saas'],
  },
  // ── Intelligence ──
  {
    id: 'analytics',
    label: 'Analytics',
    purpose: 'Traffic and conversion dashboards.',
    section: 'Intelligence',
    routes: ['/analytics'],
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    purpose: 'Fraud signals and pricing intelligence.',
    section: 'Intelligence',
    routes: ['/intelligence'],
  },
  {
    id: 'ai',
    label: 'AI engines',
    purpose: 'AI-assisted catalogue and reorder tools.',
    section: 'Intelligence',
    routes: ['/ai'],
  },
  // ── System ──
  {
    id: 'platform',
    label: 'Platform',
    purpose: 'Feature flags and rollout controls.',
    section: 'System',
    routes: ['/platform'],
  },
  {
    id: 'scale',
    label: 'Scale & security',
    purpose: 'Rate limits, upstream health, API keys.',
    section: 'System',
    routes: ['/scale'],
  },
  {
    id: 'globalization',
    label: 'Globalization',
    purpose: 'Countries, currencies and translations.',
    section: 'System',
    routes: ['/globalization'],
  },
  // ── Locked: the marketplace cannot run without these ──
  {
    id: 'dashboard',
    label: 'Dashboard',
    purpose: 'The home screen.',
    section: 'Overview',
    routes: ['/'],
    locked: 'The home screen cannot be hidden.',
  },
  {
    id: 'products',
    label: 'Products',
    purpose: 'The catalogue.',
    section: 'Catalog',
    routes: ['/products'],
    locked: 'A marketplace cannot run without its catalogue.',
  },
  {
    id: 'orders',
    label: 'Orders',
    purpose: 'Every order on the platform.',
    section: 'Catalog',
    routes: ['/orders'],
    locked: 'Orders are the business.',
  },
  {
    id: 'customers',
    label: 'Customers',
    purpose: 'Customer accounts.',
    section: 'Catalog',
    routes: ['/customers'],
    locked: 'Customer accounts must stay reachable.',
  },
  {
    id: 'sellers',
    label: 'Sellers',
    purpose: 'Seller onboarding, KYC and approval.',
    section: 'Marketplace',
    routes: ['/sellers'],
    locked: 'Sellers must stay reachable on a multi-vendor platform.',
  },
  {
    id: 'settlements',
    label: 'Settlements',
    purpose: 'Where the money is, and paying sellers.',
    section: 'Finance',
    routes: ['/payouts'],
    locked: 'Money owed to sellers must always be visible.',
  },
  {
    id: 'settings',
    label: 'Settings',
    purpose: 'This page.',
    section: 'System',
    routes: ['/settings', '/setup', '/website', '/uploaded-files', '/account'],
    locked: 'Hiding Settings would hide the switch that turns modules back on.',
  },
];

/** Stored shape: `{ [moduleId]: boolean }`. Absent = on, so a fresh install shows everything. */
export type ModuleToggles = Record<string, boolean>;

export function isModuleOn(toggles: ModuleToggles | null | undefined, id: string): boolean {
  const def = MODULES.find((m) => m.id === id);
  if (def?.locked) return true;
  return toggles?.[id] !== false;
}

/** Is this sidebar route owned by a hidden module? Routes no module claims are always shown. */
export function isRouteHidden(toggles: ModuleToggles | null | undefined, route: string): boolean {
  for (const m of MODULES) {
    if (m.locked) continue;
    const owns = m.routes.some((r) => route === r || route.startsWith(`${r}/`));
    if (owns) return !isModuleOn(toggles, m.id);
  }
  return false;
}

interface ModulesState {
  toggles: ModuleToggles | null;
  loaded: boolean;
  load: () => Promise<void>;
  set: (toggles: ModuleToggles) => void;
}

/**
 * Loaded once at shell mount and kept in memory; Settings → Modules writes
 * through and updates it so the sidebar changes without a reload.
 */
export const useModulesStore = create<ModulesState>((set, get) => ({
  toggles: null,
  loaded: false,
  load: async () => {
    if (get().loaded) return;
    try {
      const row = await api.cms.getSetting(MODULES_SETTING_KEY);
      set({ toggles: (row?.value as ModuleToggles | null) ?? {}, loaded: true });
    } catch {
      // No setting yet, or no permission: show everything. Hiding on error
      // would be the wrong failure — an admin would lose their navigation.
      set({ toggles: {}, loaded: true });
    }
  },
  set: (toggles) => set({ toggles }),
}));
